<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { BookOpen, Database, ExternalLink, Home, LogIn, Menu, MessageCircle, Search, UserRound, X } from 'lucide-vue-next'
import FloatingContact from './components/FloatingContact.vue'
import { AUTH_CHANGED_EVENT, hasActiveSession } from './services/api'

const route = useRoute()
const router = useRouter()
const menuOpen = ref(false)
const searchText = ref('')
const loggedIn = ref(hasActiveSession())

const nav = [
  { to: '/', label: '首页', icon: Home },
  { to: '/courses', label: '学习中心', icon: BookOpen },
  { to: '/customs', label: '海关数据', icon: Database },
  { to: '/forum', label: '同行问答', icon: MessageCircle }
]

const activePath = computed(() => route.path)
const isPlayerRoute = computed(() => route.name === 'course-player')
const accountLabel = computed(() => loggedIn.value ? '我的学习' : '登录')

function syncAuthState() {
  loggedIn.value = hasActiveSession()
}

function submitSearch() {
  if (!searchText.value.trim()) return
  router.push({ path: '/courses', query: { q: searchText.value.trim() } })
}

onMounted(() => {
  window.addEventListener(AUTH_CHANGED_EVENT, syncAuthState)
  window.addEventListener('storage', syncAuthState)
})
onBeforeUnmount(() => {
  window.removeEventListener(AUTH_CHANGED_EVENT, syncAuthState)
  window.removeEventListener('storage', syncAuthState)
})
</script>

<template>
  <div class="site-shell">
    <header class="site-header">
      <div class="header-inner">
        <RouterLink class="brand" to="/" aria-label="外贸365首页">
          <img src="/assets/logo.png" alt="外贸365" />
        </RouterLink>

        <nav :class="['desktop-nav', { open: menuOpen }]" aria-label="主要导航">
          <RouterLink
            v-for="item in nav"
            :key="item.to"
            :to="item.to"
            :class="{ active: item.to === '/' ? activePath === '/' : activePath.startsWith(item.to) }"
            @click="menuOpen = false"
          >
            {{ item.label }}
          </RouterLink>
          <a
            href="https://www.xuezuowaimao.com/kehu-anli-yemian/"
            target="_blank"
            rel="noopener noreferrer"
            @click="menuOpen = false"
          >同行案例</a>
          <a
            class="company-site-link"
            href="https://xuezuowaimao.com"
            target="_blank"
            rel="noopener noreferrer"
            @click="menuOpen = false"
          >
            <span>访问公司官网</span>
            <ExternalLink :size="15" :stroke-width="2" aria-hidden="true" />
          </a>
        </nav>

        <div class="header-actions">
          <RouterLink class="account-link" to="/account" :aria-label="accountLabel">
            <UserRound v-if="loggedIn" :size="18" />
            <LogIn v-else :size="18" />
            <span>{{ accountLabel }}</span>
          </RouterLink>
          <button class="menu-button" type="button" aria-label="打开导航" @click="menuOpen = !menuOpen">
            <X v-if="menuOpen" :size="22" />
            <Menu v-else :size="22" />
          </button>
        </div>
      </div>
    </header>

    <main>
      <RouterView />
    </main>

    <footer v-if="!isPlayerRoute" class="site-footer">
      <div class="footer-inner">
        <div class="footer-brand">
          <img src="/assets/logo.png" alt="外贸365" />
          <p>系统课程、贸易数据与同行经验，陪你把外贸做成一门长期生意。</p>
        </div>
        <div class="footer-links">
          <RouterLink to="/courses">课程中心</RouterLink>
          <RouterLink to="/customs">海关数据</RouterLink>
          <RouterLink to="/forum">同行问答</RouterLink>
          <a href="https://xuezuowaimao.com" target="_blank" rel="noopener">外贸365官网</a>
        </div>
        <form class="footer-search" @submit.prevent="submitSearch">
          <label for="footer-search">搜索课程</label>
          <div>
            <Search :size="18" />
            <input id="footer-search" v-model="searchText" placeholder="课程名称或讲师" />
            <button type="submit">搜索</button>
          </div>
        </form>
      </div>
      <div class="footer-bottom">© {{ new Date().getFullYear() }} 外贸365 · xuezuowaimao.com</div>
    </footer>

    <FloatingContact v-if="!isPlayerRoute" />
  </div>
</template>
